// Fenêtres partagées de répartition : commande (création/édition), scan, édition d'arrêt.
// Utilisées par la Grille Commandes, les SAC et l'Agent Ventes.
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Truck, Plus, Package, X, Trash2, CheckCircle2, ArrowRight, FileUp, Loader2, ScanLine } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_LABELS = { low: 'Basse', normal: 'Normale', high: 'Haute', urgent: 'Urgente' };

const inputClass = "w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm";


// ============ STOP FORM (memoized + functional setState to prevent focus loss) ============

const stopInputClass = "w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent placeholder-slate-500";

const StopForm = React.memo(function StopForm({ data, setData, label, itemsLocked = false }) {
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setData(prev => ({ ...prev, [name]: value }));
  }, [setData]);

  const handleItemChange = useCallback((index, field, value) => {
    setData(prev => {
      const newItems = prev.items.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      );
      return { ...prev, items: newItems };
    });
  }, [setData]);

  const handleAddItem = useCallback(() => {
    setData(prev => ({
      ...prev,
      items: [...prev.items, { bon_number: '', description: '', quantity: '', weight_kg: null }]
    }));
  }, [setData]);

  const handleRemoveItem = useCallback((index) => {
    setData(prev => ({
      ...prev,
      items: prev.items.length > 1 ? prev.items.filter((_, i) => i !== index) : prev.items
    }));
  }, [setData]);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1">Client *</label>
          <input type="text" name="client_name" value={data.client_name} onChange={handleChange}
            className={stopInputClass} placeholder="Nom du client" required data-testid={`${label}-client`} />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1">Adresse *</label>
          <input type="text" name="address" value={data.address} onChange={handleChange}
            className={stopInputClass} placeholder="123 Rue Principale" required data-testid={`${label}-address`} />
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1">Ville *</label>
          <input type="text" name="city" value={data.city} onChange={handleChange}
            className={stopInputClass} placeholder="Montreal" required data-testid={`${label}-city`} />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1">Code postal</label>
          <input type="text" name="postal_code" value={data.postal_code} onChange={handleChange}
            className={stopInputClass} placeholder="H2X 1Y6" />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1">Date *</label>
          <input type="date" name="required_date" value={data.required_date} onChange={handleChange}
            className={stopInputClass} required data-testid={`${label}-date`} />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1">Heure</label>
          <input type="text" name="required_time" value={data.required_time} onChange={handleChange}
            className={stopInputClass} placeholder="08:00" data-testid={`${label}-time`} />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1">Contact</label>
          <input type="text" name="contact_name" value={data.contact_name} onChange={handleChange}
            className={stopInputClass} placeholder="Nom du contact" />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1">Telephone</label>
          <input type="tel" name="contact_phone" value={data.contact_phone} onChange={handleChange}
            className={stopInputClass} placeholder="514-555-0100" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-400 mb-1">Articles / Bons</label>
        {itemsLocked && (
          <div className="mb-2 text-[11px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2" data-testid={`${label}-items-locked-note`}>
            Quantités et poids synchronisés avec la cueillette (la livraison porte obligatoirement la même cargaison).
          </div>
        )}
        {itemsLocked && data.items.map((item, i) => (
          <div key={i} className="flex items-center gap-2 mb-1.5 text-sm text-slate-300" data-testid={`${label}-item-locked-${i}`}>
            <span className="flex-[2] px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg">{item.bon_number || '—'}</span>
            <span className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg" data-testid={`${label}-item-locked-qty-${i}`}>{item.quantity || '—'}</span>
            <span className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg">{item.weight_kg ? `${item.weight_kg} kg` : '—'}</span>
          </div>
        ))}
        {!itemsLocked && data.items.map((item, i) => (
          <div key={i} className="flex items-center gap-2 mb-1.5">
            <input type="text" value={item.bon_number}
              onChange={e => handleItemChange(i, 'bon_number', e.target.value)}
              className={`flex-[2] ${stopInputClass}`} placeholder="No de bon" />
            <input type="text" value={item.quantity}
              onChange={e => handleItemChange(i, 'quantity', e.target.value)}
              className={`flex-1 ${stopInputClass}`} placeholder="Qte" />
            <input type="number" min="0" step="0.1" value={item.weight_kg || ''}
              onChange={e => handleItemChange(i, 'weight_kg', e.target.value ? parseFloat(e.target.value) : null)}
              className={`flex-1 ${stopInputClass}`} placeholder="Poids kg" />
            <button type="button" onClick={() => handleRemoveItem(i)}
              className="p-2 hover:bg-red-500/20 rounded text-red-400 shrink-0" disabled={data.items.length === 1}>
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
        {!itemsLocked && (
          <button type="button" onClick={handleAddItem}
            className="text-xs text-amber-500 hover:text-amber-400 flex items-center gap-1 mt-1">
            <Plus className="w-3 h-3" /> Ajouter un article
          </button>
        )}
        {(() => {
          const total = (data.items || []).reduce((s, it) => s + (parseFloat(it.weight_kg) || 0), 0);
          return total > 0 ? (
            <p className="text-[10px] text-slate-500 mt-1">
              Poids total : <span className="text-amber-400 font-semibold">{total.toFixed(1)} kg</span>
            </p>
          ) : null;
        })()}
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-400 mb-1">Notes</label>
        <textarea name="notes" value={data.notes} onChange={handleChange}
          rows={2} className={stopInputClass} placeholder="Instructions speciales..." />
      </div>
    </div>
  );
});

// ============ ORDER MODAL (Cueillette + Livraison) ============

const emptyStop = () => ({
  client_name: '', address: '', city: '', postal_code: '', province: 'QC',
  required_date: '', required_time: '', contact_name: '', contact_phone: '', notes: '',
  items: [{ bon_number: '', description: '', quantity: '', weight_kg: null }],
});

const OrderModal = ({ orderId, onClose, onSave, token, selectedDate }) => {
  const [priority, setPriority] = useState('normal');
  const [orderNotes, setOrderNotes] = useState('');
  const [pickup, setPickup] = useState({ ...emptyStop(), required_date: selectedDate });
  const [delivery, setDelivery] = useState({ ...emptyStop(), required_date: selectedDate });
  const [saving, setSaving] = useState(false);
  const [loadingOrder, setLoadingOrder] = useState(!!orderId);
  const [activeTab, setActiveTab] = useState('pickup');

  useEffect(() => {
    if (orderId) {
      (async () => {
        try {
          const response = await axios.get(`${API_URL}/api/dispatch/orders/${orderId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const order = response.data.order;
            setPriority(order.priority || 'normal');
            setOrderNotes(order.order_notes || '');

            const pickupStop = (order.stops || []).find(s => s.stop_type === 'cueillette');
            const deliveryStop = (order.stops || []).find(s => s.stop_type === 'livraison');

            if (pickupStop) {
              setPickup({
                client_name: pickupStop.client_name || '',
                address: pickupStop.address || '',
                city: pickupStop.city || '',
                postal_code: pickupStop.postal_code || '',
                province: pickupStop.province || 'QC',
                required_date: pickupStop.required_date || selectedDate,
                required_time: pickupStop.required_time || '',
                contact_name: pickupStop.contact_name || '',
                contact_phone: pickupStop.contact_phone || '',
                notes: pickupStop.notes || '',
                items: pickupStop.items?.length > 0 ? pickupStop.items : [{ bon_number: pickupStop.reference_number || '', description: '', quantity: pickupStop.quantity || (pickupStop.total_pieces ? String(pickupStop.total_pieces) : ''), weight_kg: pickupStop.weight_kg || null }],
              });
            }
            if (deliveryStop) {
              setDelivery({
                client_name: deliveryStop.client_name || '',
                address: deliveryStop.address || '',
                city: deliveryStop.city || '',
                postal_code: deliveryStop.postal_code || '',
                province: deliveryStop.province || 'QC',
                required_date: deliveryStop.required_date || selectedDate,
                required_time: deliveryStop.required_time || '',
                contact_name: deliveryStop.contact_name || '',
                contact_phone: deliveryStop.contact_phone || '',
                notes: deliveryStop.notes || '',
                items: deliveryStop.items?.length > 0 ? deliveryStop.items : [{ bon_number: '', description: '', quantity: '', weight_kg: null }],
              });
            }
        } catch (err) {
          toast.error('Erreur chargement de l\'ordre');
        } finally {
          setLoadingOrder(false);
        }
      })();
    }
  }, [orderId, token, selectedDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pickup.client_name || !pickup.address || !pickup.city) {
      toast.error('Veuillez remplir les champs obligatoires de la cueillette');
      setActiveTab('pickup');
      return;
    }
    if (!delivery.client_name || !delivery.address || !delivery.city) {
      toast.error('Veuillez remplir les champs obligatoires de la livraison');
      setActiveTab('delivery');
      return;
    }
    setSaving(true);

    const pickupItems = pickup.items.filter(i => i.bon_number || i.quantity || i.weight_kg);
    const body = {
      pickup: { ...pickup, items: pickupItems },
      delivery: { ...delivery, items: pickupItems.map(i => ({ ...i })) },
      priority,
      order_notes: orderNotes,
    };

    try {
      const url = orderId
        ? `${API_URL}/api/dispatch/orders/${orderId}`
        : `${API_URL}/api/dispatch/orders`;

      if (orderId) {
        await axios.put(url, body, { headers: { 'Authorization': `Bearer ${token}` } });
      } else {
        await axios.post(url, body, { headers: { 'Authorization': `Bearer ${token}` } });
      }
      toast.success(orderId ? 'Ordre modifie' : 'Ordre cree avec 2 arrets');
      onSave();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur de sauvegarde';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };


  if (loadingOrder) {
    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="order-modal">
      <div className="bg-slate-800 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <h2 className="text-lg font-bold" data-testid="order-modal-title">
            {orderId ? 'Modifier la commande' : 'Nouvelle commande'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-lg"><X className="w-5 h-5" /></button>
        </div>

        {/* Scrollable form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Priority + Notes */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Priorite</label>
              <select value={priority} onChange={e => setPriority(e.target.value)}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm" data-testid="order-priority">
                {Object.entries(PRIORITY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Notes generales</label>
              <input type="text" value={orderNotes} onChange={e => setOrderNotes(e.target.value)}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500"
                placeholder="Notes pour la commande..." />
            </div>
          </div>

          {/* Tabs: Cueillette / Livraison */}
          <div className="flex gap-1 bg-slate-900 rounded-lg p-1">
            <button type="button" onClick={() => setActiveTab('pickup')}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
                activeTab === 'pickup' ? 'bg-emerald-500/20 text-emerald-400' : 'text-slate-400 hover:text-white'
              }`} data-testid="tab-pickup">
              <Package className="w-4 h-4" /> Cueillette
            </button>
            <button type="button" onClick={() => setActiveTab('delivery')}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
                activeTab === 'delivery' ? 'bg-purple-500/20 text-purple-400' : 'text-slate-400 hover:text-white'
              }`} data-testid="tab-delivery">
              <Truck className="w-4 h-4" /> Livraison
            </button>
          </div>

          {/* Stop Form */}
          {activeTab === 'pickup' && <StopForm data={pickup} setData={setPickup} label="pickup" color="emerald" />}
          {activeTab === 'delivery' && (
            <>
              <StopForm data={{ ...delivery, items: pickup.items }} setData={setDelivery} label="delivery" color="purple" itemsLocked />
            </>
          )}
        </form>

        {/* Footer */}
        <div className="p-4 border-t border-slate-700 flex justify-between items-center">
          <div className="text-xs text-slate-500">
            {activeTab === 'pickup' ? '1/2 Cueillette' : '2/2 Livraison'}
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm">
              Annuler
            </button>
            {activeTab === 'pickup' ? (
              <button type="button" onClick={() => setActiveTab('delivery')}
                className="px-6 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg font-semibold text-sm flex items-center gap-2"
                data-testid="next-to-delivery">
                Livraison <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button type="button" onClick={handleSubmit} disabled={saving}
                className="px-6 py-2 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-400 disabled:opacity-50 text-sm"
                data-testid="save-order-btn">
                {saving ? 'Sauvegarde...' : (orderId ? 'Modifier' : 'Creer la commande')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ----- Modals exposés pour réutilisation par d'autres pages (voir bas du fichier) -----

// ==================== SCAN ORDER MODAL ====================

// SCANNER MODAL — export pour réutilisation dans AdminAgentVentesPage (Nouvelle commande)
export const ScanOrderModal = ({ token, scanning, setScanning, scanResult, setScanResult, onClose, onCreated }) => {
  const [creating, setCreating] = useState(false);
  const [editedResult, setEditedResult] = useState(null);
  const [currentOrder, setCurrentOrder] = useState(0);
  const API = process.env.REACT_APP_BACKEND_URL;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    setScanResult(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/api/dispatch/scan-order`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 90000,
      });
      if (res.data.success) {
        const extracted = res.data.extracted;
        // Normalize: ensure orders array exists
        let orders = extracted.orders || [];
        // Backward compat: if old format with flat stops
        if (!orders.length && extracted.stops) {
          orders = [{
            shipper_name: extracted.shipper_name || '',
            order_reference: extracted.order_reference || '',
            equipment_required: extracted.equipment_required || 'dry_van',
            equipment_details: extracted.equipment_details || '',
            special_instructions: extracted.special_instructions || '',
            urgency: extracted.urgency || 'normal',
            stops: extracted.stops,
          }];
        }
        setScanResult({ orders });
        setEditedResult({ orders: JSON.parse(JSON.stringify(orders)) });
        setCurrentOrder(0);
        const totalStops = orders.reduce((s, o) => s + (o.stops?.length || 0), 0);
        toast.success(`${orders.length} commande(s), ${totalStops} arret(s) detecte(s)`);
      } else {
        toast.error(res.data.error || 'Erreur extraction');
      }
    } catch (err) {
      toast.error('Erreur scan');
      console.error(err);
    } finally {
      setScanning(false);
    }
  };

  const updateOrderField = (field, value) => {
    setEditedResult(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy.orders[currentOrder][field] = value;
      return copy;
    });
  };

  const updateStop = (stopIdx, field, value) => {
    setEditedResult(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy.orders[currentOrder].stops[stopIdx][field] = value;
      return copy;
    });
  };

  const removeStop = (stopIdx) => {
    setEditedResult(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy.orders[currentOrder].stops.splice(stopIdx, 1);
      return copy;
    });
  };

  const removeOrder = (orderIdx) => {
    setEditedResult(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy.orders.splice(orderIdx, 1);
      if (currentOrder >= copy.orders.length) setCurrentOrder(Math.max(0, copy.orders.length - 1));
      return copy;
    });
  };

  const handleCreate = async () => {
    if (!editedResult?.orders?.length) return;
    setCreating(true);
    try {
      // Save corrections for autolearning (compare scanResult vs editedResult)
      if (scanResult?.orders) {
        for (let i = 0; i < editedResult.orders.length && i < scanResult.orders.length; i++) {
          const orig = scanResult.orders[i];
          const edited = editedResult.orders[i];
          // Flatten stops for comparison
          const origFlat = { ...(orig.stops?.[0] || {}), ...(orig.stops?.[1] || {}), order_reference: orig.order_reference };
          const editedFlat = { ...(edited.stops?.[0] || {}), ...(edited.stops?.[1] || {}), order_reference: edited.order_reference };
          try {
            axios.post(`${API}/api/scan/corrections`, {
              scan_type: 'dispatch',
              original_extraction: origFlat,
              corrected_data: editedFlat,
            }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
          } catch (e) { /* non-blocking */ }
        }
      }

      const res = await axios.post(`${API}/api/dispatch/scan-order/create-stops`, editedResult, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        toast.success(`${res.data.orders_created} commande(s), ${res.data.total_stops} arret(s) cree(s)`);
        onCreated();
      }
    } catch (err) {
      toast.error('Erreur creation');
    } finally {
      setCreating(false);
    }
  };

  const order = editedResult?.orders?.[currentOrder];
  const totalOrders = editedResult?.orders?.length || 0;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="scan-modal">
      <div className="bg-slate-900 rounded-2xl border border-slate-800 w-full max-w-3xl my-8">
        <div className="p-5 border-b border-slate-700 flex items-center justify-between">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-amber-500" /> Scanner une demande de transport
          </h2>
          <button onClick={onClose} className="p-1 hover:text-white text-slate-400"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          {!scanResult && (
            <label className="flex flex-col items-center justify-center py-12 border-2 border-dashed border-slate-700 hover:border-amber-500/50 rounded-xl cursor-pointer transition-colors group">
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" className="hidden" onChange={handleUpload} disabled={scanning} />
              {scanning ? (
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-10 h-10 text-amber-500 animate-spin" />
                  <p className="text-amber-400 font-medium">Analyse du document en cours...</p>
                  <p className="text-xs text-slate-500">L'IA lit et extrait les informations</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <FileUp className="w-10 h-10 text-slate-500 group-hover:text-amber-500 transition-colors" />
                  <p className="text-slate-400 group-hover:text-amber-400 font-medium">Glissez ou cliquez pour uploader</p>
                  <p className="text-xs text-slate-500">PDF, photo ou scan d'un bon de commande</p>
                </div>
              )}
            </label>
          )}

          {editedResult && order && (
            <div className="space-y-4">
              {/* Order navigation tabs */}
              {totalOrders > 1 && (
                <div className="flex items-center gap-2 flex-wrap">
                  {editedResult.orders.map((o, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentOrder(i)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${
                        i === currentOrder ? 'bg-amber-500 text-black' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                      }`}
                    >
                      Commande {i + 1}
                      <span className="text-[10px] opacity-70">({o.stops?.length || 0} arrets)</span>
                      {totalOrders > 1 && (
                        <button
                          onClick={(ev) => { ev.stopPropagation(); removeOrder(i); }}
                          className="ml-1 hover:text-red-400"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </button>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] text-slate-500 mb-1">Reference</label>
                  <input className={inputClass} value={order.order_reference || ''} onChange={e => updateOrderField('order_reference', e.target.value)} />
                </div>
                <div>
                  <label className="block text-[10px] text-slate-500 mb-1">Client/Expediteur</label>
                  <input className={inputClass} value={order.shipper_name || ''} onChange={e => updateOrderField('shipper_name', e.target.value)} />
                </div>
                <div>
                  <label className="block text-[10px] text-slate-500 mb-1">Equipement</label>
                  <select className={inputClass} value={order.equipment_required || 'dry_van'} onChange={e => updateOrderField('equipment_required', e.target.value)}>
                    <option value="dry_van">Dry Van</option>
                    <option value="flatbed">Flatbed</option>
                    <option value="reefer">Reefer</option>
                    <option value="step_deck">Step Deck</option>
                    <option value="citerne">Citerne</option>
                  </select>
                </div>
              </div>

              {order.equipment_details && (
                <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-2 text-xs text-amber-400">
                  Equipement special: {order.equipment_details}
                </div>
              )}

              <h3 className="text-sm font-semibold text-white">
                Arrets - Commande {currentOrder + 1}{totalOrders > 1 ? ` / ${totalOrders}` : ''} ({order.stops?.length || 0} arrets)
              </h3>
              {order.stops?.map((stop, idx) => (
                <div key={idx} className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <select className="bg-slate-700 border-none rounded-full text-xs px-2 py-0.5 text-white" value={stop.stop_type} onChange={e => updateStop(idx, 'stop_type', e.target.value)}>
                        <option value="cueillette">CUEILLETTE</option>
                        <option value="livraison">LIVRAISON</option>
                      </select>
                    </div>
                    <button onClick={() => removeStop(idx)} className="p-1 hover:text-red-400 text-slate-500"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-0.5">Client</label>
                      <input className={inputClass} value={stop.client_name || ''} onChange={e => updateStop(idx, 'client_name', e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-0.5">Ref/PO</label>
                      <input className={inputClass} value={stop.reference_number || ''} onChange={e => updateStop(idx, 'reference_number', e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] text-slate-500 mb-0.5">Adresse</label>
                    <input className={inputClass} value={stop.address || ''} onChange={e => updateStop(idx, 'address', e.target.value)} />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-0.5">Ville</label>
                      <input className={inputClass} value={stop.city || ''} onChange={e => updateStop(idx, 'city', e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-0.5">Code postal</label>
                      <input className={inputClass} value={stop.postal_code || ''} onChange={e => updateStop(idx, 'postal_code', e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-0.5">Date</label>
                      <input type="date" className={inputClass} value={stop.required_date || ''} onChange={e => updateStop(idx, 'required_date', e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-0.5">Heure</label>
                      <input type="time" className={inputClass} value={stop.required_time || ''} onChange={e => updateStop(idx, 'required_time', e.target.value)} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-0.5">Qte</label>
                      <input className={inputClass} value={stop.quantity || ''} onChange={e => updateStop(idx, 'quantity', e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-0.5">Poids (kg)</label>
                      <input className={inputClass} value={stop.weight_kg || ''} onChange={e => updateStop(idx, 'weight_kg', e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-0.5">#Camion/BOL</label>
                      <input className={inputClass} value={stop.truck_number || ''} onChange={e => updateStop(idx, 'truck_number', e.target.value)} />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-0.5">Contact</label>
                      <input className={inputClass} value={stop.contact_name || ''} onChange={e => updateStop(idx, 'contact_name', e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-0.5">Tel</label>
                      <input className={inputClass} value={stop.contact_phone || ''} onChange={e => updateStop(idx, 'contact_phone', e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-0.5">Email</label>
                      <input className={inputClass} value={stop.contact_email || ''} onChange={e => updateStop(idx, 'contact_email', e.target.value)} />
                    </div>
                  </div>
                  {stop.notes && (
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-0.5">Notes</label>
                      <input className={inputClass} value={stop.notes} onChange={e => updateStop(idx, 'notes', e.target.value)} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {editedResult?.orders?.length > 0 && (
          <div className="p-5 border-t border-slate-700 flex justify-between">
            <button onClick={() => { setScanResult(null); setEditedResult(null); }} className="px-4 py-2 text-sm text-slate-400 hover:text-white">Rescanner</button>
            <button onClick={handleCreate} disabled={creating}
              className="px-6 py-2 bg-amber-500 text-black font-bold rounded-lg hover:bg-amber-400 disabled:opacity-50 flex items-center gap-2 text-sm"
              data-testid="create-scanned-stops-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {creating ? 'Creation...' : `Creer ${totalOrders} commande(s)`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};


// ============ EDIT STOP MODAL (for driver-created stops) ============

const EditStopModal = ({ stop, onClose, onSave, token }) => {
  const [form, setForm] = useState({
    client_name: stop.client_name || '',
    address: stop.address || '',
    city: stop.city || '',
    stop_type: stop.stop_type || 'cueillette',
    required_date: stop.required_date || '',
    required_time: stop.required_time || '',
    notes: stop.notes || '',
    contact_name: stop.contact_name || '',
    contact_phone: stop.contact_phone || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/api/dispatch/stops/${stop.id}`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onSave();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent placeholder-slate-500";

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-md max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="edit-stop-modal">
        <div className="p-5 border-b border-slate-700 flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">Modifier l'arret</h3>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded text-slate-400"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Type</label>
            <select value={form.stop_type} onChange={e => setForm(f => ({...f, stop_type: e.target.value}))} className={inputClass} data-testid="edit-stop-type">
              <option value="cueillette">Cueillette</option>
              <option value="livraison">Livraison</option>
              <option value="pause">Pause</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Client</label>
            <input type="text" value={form.client_name} onChange={e => setForm(f => ({...f, client_name: e.target.value}))} className={inputClass} placeholder="Nom du client" data-testid="edit-stop-client" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Adresse</label>
            <input type="text" value={form.address} onChange={e => setForm(f => ({...f, address: e.target.value}))} className={inputClass} placeholder="Adresse" data-testid="edit-stop-address" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Ville</label>
              <input type="text" value={form.city} onChange={e => setForm(f => ({...f, city: e.target.value}))} className={inputClass} />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Date requise</label>
              <input type="date" value={form.required_date} onChange={e => setForm(f => ({...f, required_date: e.target.value}))} className={inputClass} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Contact</label>
              <input type="text" value={form.contact_name} onChange={e => setForm(f => ({...f, contact_name: e.target.value}))} className={inputClass} placeholder="Nom" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Telephone</label>
              <input type="text" value={form.contact_phone} onChange={e => setForm(f => ({...f, contact_phone: e.target.value}))} className={inputClass} placeholder="514-..." />
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} className={inputClass} rows={2} placeholder="Notes..." />
          </div>
        </div>
        <div className="p-5 border-t border-slate-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 text-sm">Annuler</button>
          <button onClick={handleSave} disabled={saving} className="px-6 py-2 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-400 disabled:opacity-50 text-sm" data-testid="save-stop-btn">
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
      </div>
    </div>
  );
};

export { OrderModal as DispatchOrderModal };
export { ScanOrderModal as DispatchScanOrderModal };
export { EditStopModal as DispatchEditStopModal };
